import React, { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next';
import {
  iamTokenManager,
  iamAccountManager,
  agentTemplateHooks,
  agentUserVoiceListHooks,
  iamAccountHooks,
  agentManager,
} from '@/manager';
import { useStyleEditorMicroApp } from '@/hooks/use-style-editor-microapp';
import StyleEditorMicroApp from "@bhb-frontend/style-editor-microapp";
import { useToast } from '@/hooks/use-toast';
import { useParams } from 'react-router-dom';

interface StyleEditorMicroAppPageProps {
  uuid?: string;
}

const StyleEditorMicroAppPage: React.FC<StyleEditorMicroAppPageProps> = ({ uuid }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const params = useParams();
  // 设置当前智能体ID
  agentManager.setAgentId(params.id);
  const [triggerId, setTriggerId] = useState("");
  const { account } = iamAccountHooks.useData();
  // 用户资产数字人和音频列表
  const { agentUserActors, agentUserVoices, loading: agentUserLoading } = agentUserVoiceListHooks.useUserActorAndVoiceData();
  // 模板相关
  const { save } = agentTemplateHooks.useSaveAgentTemplate();
  const { fetchDetail } = agentTemplateHooks.useTemplateDetail();
  const { update } = agentTemplateHooks.useUpdateAgentTemplate();
  // 微应用 Hook
  const {
    env,
    initialAppLoading,
    enablePresetTemplates,
    handleMicroAppMessage,
    handleMounted,
    handleUnmounted,
    openEditor
  } = useStyleEditorMicroApp({
    account,
    openPresetTemplates: true,
    openPublicActors: true,
    openPublicVoices: true,
    actors: agentUserActors?.filter(item => item.generateStatus === 2).map(item => ({
      id: item.id,
      name: item.name,
      coverUrl: item.image,
      renderUrl: item.image,
      width: item.width,
      height: item.height,
      modelId: item.modelId
    })),
    voices: agentUserVoices.map((item) => ({
      id: item.voiceTtsId,
      name: item.name,
      exampleUrl: item.audioUrl,
    })),
    onSaveSuccess: async ({ styleSaveParams, actorModelId }) => {
      if (!triggerId) {
        // 构建新建保存参数
        const templateName = enablePresetTemplates.find(item => item.templateId === styleSaveParams.selectedId)?.templateName || '';

        const createParams = {
          styleId: styleSaveParams.styleId,
          templateName,
          actorModelId,
          voiceModelId: styleSaveParams.voiceId || '',
          coverUrl: styleSaveParams.coverUrl,
          speedRatio: styleSaveParams.speedRatio,
          scene: styleSaveParams.scene,
          templateId: styleSaveParams.templateId,
        }
        console.log('[miuta]构建创建请求参数', { createParams, styleSaveParams, enablePresetTemplates })
        await save(createParams, {
          userId: account?.id || '',
          type: 1,
          agentId: params.id,
          autoRefresh: false
        });
        (window as any).MiutaBridge?.postMessage?.('WEBVIEW_SAVE_SUCCESS');
      } else {
        // 构建更新保存参数
        const updateParams = {
          uuid: triggerId,
          styleId: styleSaveParams.styleId,
          coverUrl: styleSaveParams.coverUrl,
          actorModelId,
          voiceModelId: styleSaveParams.voiceId || '',
          speedRatio: styleSaveParams.speedRatio,
          scene: styleSaveParams.scene,
        }
        console.log('[miuta]构建更新请求参数', { updateParams, styleSaveParams, enablePresetTemplates })
        await update(updateParams, {
          userId: account?.id || '',
          type: 1,
          agentId: params.id,
          autoRefresh: false
        });
        setTriggerId("");
        (window as any).MiutaBridge?.postMessage?.('WEBVIEW_SAVE_SUCCESS');
      }
    },
    onClose: async () => {
      setTriggerId('');
      (window as any).MiutaBridge?.postMessage?.('WEBVIEW_CLOSE');
    },
  });
  const openedOnceRef = useRef(false);

  // 统一的数据加载状态
  const isLoading = agentUserLoading || initialAppLoading;

  useEffect(() => {
    if (openedOnceRef.current) return;
    if (isLoading) return;
    if (enablePresetTemplates.length > 0) {
      handleEdit(uuid);
    } else {
      toast.toast({
        description: t("agent_creation.subscription_settings.no_preset_template"),
      });
    }
  }, [isLoading, enablePresetTemplates, openEditor, uuid]);

  // 打开编辑模板
  const handleEdit = async (uuid?: string) => {
    openedOnceRef.current = true;
    setTriggerId(uuid || "");
    if (uuid) {
      // 获取模板详情
      const detail = await fetchDetail(uuid);
      // 取出数字人信息
      const actorInfo = agentUserActors?.find(item => item.modelId === detail.actorModelId)
      // 处理编辑参数
      const editIdParam = {
        coverUrl: detail.coverUrl,
        styleId: detail.styleModelId,
        digitalHumanInfo: {
          id: actorInfo?.id,
          name: detail.actorName,
          width: actorInfo?.width,
          height: actorInfo?.height,
          coverUrl: detail.actorPicUrl,
          renderUrl: detail.actorPicUrl,
        },
        voiceInfo: {
          id: detail.voiceModelId,
          name: detail.voiceName,
          coverUrl: detail.coverUrl,
          exampleUrl: detail.voiceSampleUrl,
          speedRatio: detail.speedRatio,
        }
      }
      openEditor({ editIdParam });
    } else {
      openEditor({});
    }
    (window as any).MiutaBridge?.postMessage?.('WEBVIEW_MOUNTED');
  }

  return (
    <div
      id="micro-app-portal"
      className='z-[999999] scrollbar-thin'
      style={{ pointerEvents: 'auto' }}
      // 阻止滚动事件冒泡到 document/body
      onWheel={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <StyleEditorMicroApp
        name="style-editor-microapp"
        env={env}
        onMicroAppMessage={handleMicroAppMessage}
        onMounted={handleMounted}
        onUnmounted={handleUnmounted}
      />
    </div>
  );
}

export const StyleEditorMicroAppWebviewPage: React.FC = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const uuid = searchParams.get('uuid') || '';
  const token = searchParams.get('token');
  const account = searchParams.get('account');
  const parsedAccount = JSON.parse(decodeURIComponent(account));
  iamTokenManager.updateToken(token);
  iamAccountManager.update(parsedAccount);
  return <StyleEditorMicroAppPage uuid={uuid} />;
};
